import { useEffect } from 'react';

const SCRIPT_SRCS = [
  '/assets/js/vendor/jquery-3.6.0.min.js',
  '/assets/js/slick.min.js',
  '/assets/js/bootstrap.min.js',
  '/assets/js/wow.min.js',
  '/assets/js/jquery.magnific-popup.min.js',
  '/assets/js/jquery.nice-select.min.js',
  '/assets/js/imagesloaded.pkgd.min.js',
  '/assets/js/isotope.pkgd.min.js',
  '/assets/js/jquery-ui.min.js',
  '/assets/js/main.js',
] as const;

declare global {
  interface Window {
    jQuery?: JQueryStatic;
    $?: JQueryStatic;
    WOW?: new () => { init: () => void };
  }
}

type JQueryStatic = ((sel?: unknown) => JQueryInstance) & {
  fn: Record<string, unknown>;
};

type JQueryInstance = {
  slick?: (opts?: unknown) => JQueryInstance;
  magnificPopup?: (opts?: unknown) => JQueryInstance;
  niceSelect?: () => JQueryInstance;
  isotope?: (opts?: unknown) => JQueryInstance;
  imagesLoaded?: (cb?: () => void) => JQueryInstance;
  slider?: (opts?: unknown) => JQueryInstance;
  counterUp?: (opts?: unknown) => JQueryInstance;
  off: (...args: unknown[]) => JQueryInstance;
  on: (...args: unknown[]) => JQueryInstance;
  trigger: (...args: unknown[]) => JQueryInstance;
};

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-ileafs-src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') resolve();
      else existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = false;
    el.dataset.ileafsSrc = src;
    el.onload = () => {
      el.dataset.loaded = '1';
      resolve();
    };
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(el);
  });
}

async function ensureScripts() {
  for (const src of SCRIPT_SRCS) {
    await loadScript(src);
  }
}

function destroySlick() {
  const $ = window.jQuery;
  if (!$ || !$.fn?.slick) return;
  document.querySelectorAll('.slick-initialized').forEach((el) => {
    try {
      ($(el) as JQueryInstance).slick?.('unslick');
    } catch {
      /* ignore */
    }
  });
}

/**
 * Loads template vendor JS once, then re-runs main.js initializers on each route change
 * so sliders / isotope / wow / nice-select stay pixel-perfect and responsive.
 */
export function useTemplateRuntime(pathname: string) {
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await ensureScripts();
        if (cancelled) return;

        requestAnimationFrame(() => {
          if (cancelled) return;
          destroySlick();

          // Re-run main.js so jQuery plugins bind to the new page DOM.
          const main = document.querySelector<HTMLScriptElement>(
            'script[data-ileafs-src="/assets/js/main.js"]',
          );
          if (main) main.remove();

          void loadScript(`/assets/js/main.js?t=${Date.now()}`).then(() => {
            if (cancelled) return;
            // Template listens for window "load" (already past in SPA) — force bg images + plugins.
            window.dispatchEvent(new Event('load'));
            // React owns the mobile drawer — strip jQuery toggle handlers that fight SPA state.
            try {
              window.jQuery?.('.vs-menu-toggle')?.off('click');
            } catch {
              /* ignore */
            }
            window.scrollTo(0, 0);
          });
        });
      } catch (err) {
        console.error('[template-runtime]', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname]);
}
